import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditableColumnDef, EditableTable } from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { NonMandatoryCriterionDetails } from "../../../sc-models/scope-certificate-details-dto";
import { OptionType } from "../../../../../../shared/components/form-builder/form-config.model";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useParams } from "react-router-dom";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";

interface NonMandatoryCriterionDetailsFormProps {
  nonMandatoryCriterionData: NonMandatoryCriterionDetails[];
  setNonMandatoryCriterionData: (data: NonMandatoryCriterionDetails[]) => void;
  scStatus?: string;
}

const isNaLike = (v?: string | null) => {
  if (!v) return false;
  const lower = v.trim().toLowerCase();
  return ["na", "n/a", "na.", "na ", "not available", "null", "nil", "none", "n a", "n.a"].includes(lower);
};

const naOnlyValidator = (value?: string | null) => {
  if (!value) return true;
  const lower = value.trim().toLowerCase();
  if (!isNaLike(value)) return true;
  return lower === "na";
};

const compareCriterionNumbers = (a: string, b: string): number => {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  if (a.toLowerCase() === "na") return 1;
  if (b.toLowerCase() === "na") return -1;

  const partsA = a.split('.');
  const partsB = b.split('.');
  const maxLength = Math.max(partsA.length, partsB.length);

  for (let i = 0; i < maxLength; i++) {
    const partA = partsA[i];
    const partB = partsB[i];

    if (partA === undefined) return -1;
    if (partB === undefined) return 1;

    const numA = Number(partA);
    const numB = Number(partB);

    if (!isNaN(numA) && !isNaN(numB)) {
      if (numA !== numB) {
        return numA - numB;
      }
    } else {
      const cmp = partA.localeCompare(partB, undefined, { numeric: true });
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
};

const validationSchema = Yup.object().shape({
  scNonMandatoryCriterionStandard: Yup.string()
    .required("SC Non-Mandatory Criterion Standard is required")
    .test("na-lower-only", 'Only lowercase "na" allowed for NA value', (v) => naOnlyValidator(v)),
  scNonMandatoryCriterionVersion: Yup.string()
    .required("SC Non-Mandatory Criterion Version is required")
    .test("na-lower-only", 'Only lowercase "na" allowed for NA value', (v) => naOnlyValidator(v)),
  scNonMandatoryCriterionNumber: Yup.string()
    .required("SC Non-Mandatory Criterion Number is required")
    .test("na-lower-only", 'Only lowercase "na" allowed for NA value', (v) => naOnlyValidator(v)),
  scNonMandatoryCriterionStatus: Yup.string()
    .required("SC Non-Mandatory Criterion Status is required")
    .test("na-lower-only", 'Only lowercase "na" allowed for NA value', (v) => naOnlyValidator(v)),
});

const NonMandatoryCriterionDetailsForm: React.FC<NonMandatoryCriterionDetailsFormProps> = ({
  nonMandatoryCriterionData,
  setNonMandatoryCriterionData,
  scStatus,
}) => {
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const isStatusLocked = ['E', 'W', 'S'].includes(scStatus || '');
  const isEditingDisabled = hasSubmitSC || isStatusLocked;
  const [data, setData] = useState<NonMandatoryCriterionDetails[]>(nonMandatoryCriterionData);
  const [tableData, setTableData] = useState<NonMandatoryCriterionDetails[]>(nonMandatoryCriterionData);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [scNonMandatoryCriterionStandardValues, setScNonMandatoryCriterionStandardValues] = useState<OptionType[]>([]);
  const [nonMandatoryCriterionVersionOptions, setNonMandatoryCriterionVersionOptions] = useState<OptionType[]>([]);
  const [scNonMandatoryCriterionNumbers, setScNonMandatoryCriterionNumbers] = useState<OptionType[]>([]);
  const [scNonMandatoryCriterionStatusValues, setScNonMandatoryCriterionStatusValues] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { notify } = useNotification();
  const { setRestrictPopUpSave, isRestrictPopUpSave } = useTransactionCertificateStore();

  // Fetch non-mandatory criterion standard values from DTrackIt API
  const getNonMandatoryCriterionStandardValues = async () => {
    try {
      setLoading(true);
      const response = await dtrackItServiceV4.getNonMandatoryCriterionStandardValues();
      if (response?.data) {
        const transformedOptions = response.data.map((item: any) => ({
          label: item.code_1,
          value: item.code_1,
          code: item.code_1
        }));
        setScNonMandatoryCriterionStandardValues(transformedOptions);
      }
    } catch (error) {
      console.error('Error fetching non-mandatory criterion standard values:', error);
      notify('Failed to load non-mandatory criterion standard options', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch non-mandatory criterion standard version values from DTrackIt API based on selected standard
  const getNonMandatoryCriterionStandardVersions = async (standard: string) => {
    try {
      if (!standard) {
        setNonMandatoryCriterionVersionOptions([]);
        return;
      }
      
      const response = await dtrackItServiceV4.getNonMandatoryCriterionStandardVersions(standard);
      if (response?.data) {
        const transformedOptions = response.data.map((item: any) => ({
          label: item.code_1,
          value: item.code_1,
          code: item.code_1
        }));
        setNonMandatoryCriterionVersionOptions(transformedOptions);
      } else {
        setNonMandatoryCriterionVersionOptions([]);
      }
    } catch (error) {
      console.error('Error fetching non-mandatory criterion standard versions:', error);
      notify('Failed to load non-mandatory criterion standard version options', 'error');
      setNonMandatoryCriterionVersionOptions([]);
    }
  };

  // Fetch non-mandatory criterion numbers from DTrackIt API
  const getNonMandatoryCriterionNumbers = async () => {
    try {
      setLoading(true);
      const response = await dtrackItServiceV4.getNonMandatoryCriterionNumbers();
      if (response?.data) {
        const transformedOptions = response.data.map((item: any) => ({
          label: item.title 
            ? (item.type ? `${item.code_1} - ${item.title} (${item.type})` : `${item.code_1} - ${item.title}`) 
            : item.code_1,
          value: item.code_1,
          code: item.code_1,
          title: item.title,
          type: item.type
        }));
        transformedOptions.sort((a: any, b: any) => compareCriterionNumbers(a.code, b.code));
        setScNonMandatoryCriterionNumbers(transformedOptions);
      }
    } catch (error) {
      console.error('Error fetching non-mandatory criterion numbers:', error);
      notify('Failed to load non-mandatory criterion number options', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch non-mandatory criterion status values from DTrackIt API
  const getNonMandatoryCriterionStatusValues = async () => {
    try {
      setLoading(true);
      const response = await dtrackItServiceV4.getNonMandatoryCriterionStatusValues();
      if (response?.data) {
        const transformedOptions = response.data.map((item: any) => ({
          label: item.code_1,
          value: item.code_1,
          code: item.code_1
        }));
        setScNonMandatoryCriterionStatusValues(transformedOptions);
      }
    } catch (error) {
      console.error('Error fetching non-mandatory criterion status values:', error);
      notify('Failed to load non-mandatory criterion status options', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik<NonMandatoryCriterionDetails>({
    initialValues: {
      scNonMandatoryCriterionStandard: "",
      scNonMandatoryCriterionStandardDescription: "",
      scNonMandatoryCriterionVersion: "",
      scNonMandatoryCriterionNumber: "",
      scNonMandatoryCriterionStatus: "",
      scNonMandatoryCriterionStatusDescription: "",
      scNonMandatoryCriterionDescription: "",
      scNonMandatoryCriterionType: "",
    },
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true, // This ensures formik reinitializes when data changes
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {

    if(columnId === 'scNonMandatoryCriterionStatus') {
      let optionCode = scNonMandatoryCriterionStatusValues.find(val => val.value === value)?.code || ""
      formik.setFieldValue('scNonMandatoryCriterionStatus', value);
      formik.setFieldValue('scNonMandatoryCriterionStatusDescription', value);
      // Trigger validation after setting the value
      formik.validateField('scNonMandatoryCriterionStatus');
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, ['scNonMandatoryCriterionStatus']: value, 'scNonMandatoryCriterionStatusDescription': value } : row
        )
      );
    } else if(columnId === 'scNonMandatoryCriterionStandard') {
      let optionCode = scNonMandatoryCriterionStandardValues.find(val => val.value === value)?.code || ""
      formik.setFieldValue('scNonMandatoryCriterionStandard', value);
      formik.setFieldValue('scNonMandatoryCriterionStandardDescription', value);
      // Trigger validation after setting the value
      formik.validateField('scNonMandatoryCriterionStandard');
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, ['scNonMandatoryCriterionStandard']: value, 'scNonMandatoryCriterionStandardDescription': value } : row
        )
      );
    } else {
      formik.setFieldValue(columnId, value);
      // Trigger validation after setting the value
      formik.validateField(columnId);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    }
  };

  const handleAddNewRow = () => {
    const newRow: NonMandatoryCriterionDetails = {
      scNonMandatoryCriterionStandard: "",
      scNonMandatoryCriterionStandardDescription: "",
      scNonMandatoryCriterionVersion: "",
      scNonMandatoryCriterionNumber: "",
      scNonMandatoryCriterionStatus: "",
      scNonMandatoryCriterionStatusDescription: "",
      scNonMandatoryCriterionDescription: "",
      scNonMandatoryCriterionType: "",
    };
    
    setRestrictPopUpSave(true);
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    
    // Ensure formik is properly initialized
    formik.setValues(newRow);
    formik.setTouched({});
    formik.setErrors({});
  };

  const handleCancelEdit = async () => {
    setRestrictPopUpSave(false);
    setData(tableData);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    // Check if required fields are filled
    const { scNonMandatoryCriterionStandard, scNonMandatoryCriterionVersion, scNonMandatoryCriterionNumber, scNonMandatoryCriterionStatus } = formik.values;
    
    const missingFields = [];
    if (!scNonMandatoryCriterionStandard) missingFields.push('SC Non-Mandatory Criterion Standard');
    if (!scNonMandatoryCriterionVersion) missingFields.push('SC Non-Mandatory Criterion Version');
    if (!scNonMandatoryCriterionNumber) missingFields.push('SC Non-Mandatory Criterion Number');
    if (!scNonMandatoryCriterionStatus) missingFields.push('SC Non-Mandatory Criterion Status');
    
    if (missingFields.length > 0) {
      notify(`Please fill in: ${missingFields.join(', ')}`, 'error');
      return;
    }

    // Business rule: if first column is not 'na', other columns must not be 'na'. Block save and show offending keys
    if (String(scNonMandatoryCriterionStandard).toLowerCase() !== 'na') {
      const offendingKeys: string[] = [];
      if (String(scNonMandatoryCriterionVersion).toLowerCase() === 'na') offendingKeys.push('scNonMandatoryCriterionVersion');
      if (String(scNonMandatoryCriterionNumber).toLowerCase() === 'na') offendingKeys.push('scNonMandatoryCriterionNumber');
      if (String(scNonMandatoryCriterionStatus).toLowerCase() === 'na') offendingKeys.push('scNonMandatoryCriterionStatus');
      if (offendingKeys.length > 0) {
        notify(`Invalid 'na' in: ${offendingKeys.join(', ')}`, 'error');
        return;
      }
    }

    // Check for duplicate criterion numbers
    const currentCriterionNo = String(scNonMandatoryCriterionNumber);
    const duplicateExists = data.some((row, index) => 
      index !== editingRowIndex && 
      row.scNonMandatoryCriterionNumber && 
      String(row.scNonMandatoryCriterionNumber) === currentCriterionNo
    );
    
    if (duplicateExists) {
      notify('SC Non-Mandatory Criterion No. must be unique across all rows', 'error');
      return;
    }

    if (editingRowIndex === null) {
      notify('No row is being edited', 'error');
      return;
    }

    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    
    setData(newData);
    setTableData(newData);
    setRestrictPopUpSave(false);
    
    // Call the parent function to update the data
    setNonMandatoryCriterionData(newData);
    
    setEditingRowIndex(-1);
    formik.resetForm();
    
    notify('Row saved successfully', 'success');
  };

  useEffect(() => {
    setData(nonMandatoryCriterionData);
    setTableData(nonMandatoryCriterionData);
    // Clear version options on mount (cascading — loaded when user picks a standard)
    setNonMandatoryCriterionVersionOptions([]);

    // Fetch all static reference data in parallel — reduces wait time to the slowest single call
    const loadReferenceData = async () => {
      setLoading(true);
      try {
        const [standardsResp, numbersResp, statusResp] = await Promise.all([
          dtrackItServiceV4.getNonMandatoryCriterionStandardValues(),
          dtrackItServiceV4.getNonMandatoryCriterionNumbers(),
          dtrackItServiceV4.getNonMandatoryCriterionStatusValues(),
        ]);

        if (standardsResp?.data) {
          setScNonMandatoryCriterionStandardValues(
            standardsResp.data.map((item: any) => ({ label: item.code_1, value: item.code_1, code: item.code_1 }))
          );
        }
        if (numbersResp?.data) {
          const transformedOptions = numbersResp.data.map((item: any) => ({
            label: item.title 
              ? (item.type ? `${item.code_1} - ${item.title} (${item.type})` : `${item.code_1} - ${item.title}`) 
              : item.code_1,
            value: item.code_1,
            code: item.code_1,
            title: item.title,
            type: item.type
          }));
          transformedOptions.sort((a: any, b: any) => compareCriterionNumbers(a.code, b.code));
          setScNonMandatoryCriterionNumbers(transformedOptions);
        }
        if (statusResp?.data) {
          setScNonMandatoryCriterionStatusValues(
            statusResp.data.map((item: any) => ({ label: item.code_1, value: item.code_1, code: item.code_1 }))
          );
        }
      } catch (error) {
        console.error('Error fetching non-mandatory criterion reference data:', error);
        notify('Failed to load non-mandatory criterion options', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadReferenceData();

    return (() => {
      setRestrictPopUpSave(false);
    });
  }, [nonMandatoryCriterionData]);



  const columns: EditableColumnDef<NonMandatoryCriterionDetails>[] = [
    {
      accessorKey: "scNonMandatoryCriterionStandard",
      header: "Criterion Standard",
      Header: (
        <span>
          <i style={{ color: 'red' }}>* </i>
          <abbr title="SC Non-Mandatory Criterion Standard" style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
            Criterion Standard
          </abbr>
        </span>
      ),
      renderEditCell: ({ value, onChange, rowIndex }) => (
        <CustomSelect
          name="scNonMandatoryCriterionStandard"
          value={value}
          placeholder="Select Non-Mandatory Criterion Standard"
          options={scNonMandatoryCriterionStandardValues}
          onChange={(e) => {
            const newVal = e.target.value;
            // Prevent selecting 'na' if there is already any data row present
            const hasExistingData = tableData && tableData.length > 0;
            if (String(newVal).toLowerCase() === 'na' && hasExistingData) {
              notify("'na' cannot be selected when data already exists", 'error');
              return;
            }
            onChange(newVal);
            
            // Check if "na" is selected
            if (String(newVal).toLowerCase() === "na") {
              // Set all other fields to "na" and disable them
              formik.setFieldValue("scNonMandatoryCriterionVersion", "na");
              formik.setFieldValue("scNonMandatoryCriterionNumber", "na");
              formik.setFieldValue("scNonMandatoryCriterionStatus", "na");
              formik.setFieldValue("scNonMandatoryCriterionDescription", "na");
              formik.setFieldValue("scNonMandatoryCriterionType", "na");
              setNonMandatoryCriterionVersionOptions([]);
              setData((prevData) =>
                prevData.map((row, idx) =>
                  idx === rowIndex
                    ? {
                        ...row,
                        scNonMandatoryCriterionStandard: newVal as string,
                        scNonMandatoryCriterionVersion: "na",
                        scNonMandatoryCriterionNumber: "na",
                        scNonMandatoryCriterionStatus: "na",
                        scNonMandatoryCriterionDescription: "na",
                        scNonMandatoryCriterionType: "na",
                      }
                    : row
                )
              );
            } else {
              // Fetch versions for the selected standard - convert to string for API call
              if (newVal) {
                getNonMandatoryCriterionStandardVersions(String(newVal));
              } else {
                setNonMandatoryCriterionVersionOptions([]);
              }
            }
          }}
        />
      ),
    },
    {
      accessorKey: "scNonMandatoryCriterionVersion",
      header: "Criterion Version",
      Header: (
        <span>
          <i style={{ color: 'red' }}>* </i>
          <abbr title="SC Non-Mandatory Criterion Version" style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
            Criterion Version
          </abbr>
        </span>
      ),
      renderEditCell: ({ value, onChange, rowIndex }) => {
        // Determine NA state per row: use formik values only for the row being edited
        const isEditingThisRow = editingRowIndex === rowIndex;
        const rowStandard = isEditingThisRow
          ? formik.values.scNonMandatoryCriterionStandard
          : (tableData[rowIndex]?.scNonMandatoryCriterionStandard || "");
        const isStandardNA = String(rowStandard).toLowerCase() === "na";

        const displayValue = isStandardNA ? "na" : (value || "");

        return (
          <CustomSelect
            name="scNonMandatoryCriterionVersion"
            value={displayValue}
            placeholder="Select Non-Mandatory Criterion Version"
            options={isStandardNA ? [{ value: "na", label: "na" }] : nonMandatoryCriterionVersionOptions}
            disabled={isStandardNA}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      },
    },
    {
      accessorKey: "scNonMandatoryCriterionNumber",
      header: "Criterion Number",
      Header: (
        <span>
          <i style={{ color: 'red' }}>* </i>
          <abbr title="SC Non-Mandatory Criterion Number" style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
            Criterion Number
          </abbr>
        </span>
      ),
      renderEditCell: ({ value, onChange, rowIndex }) => {
        const isEditingThisRow = editingRowIndex === rowIndex;
        const rowStandard = isEditingThisRow
          ? formik.values.scNonMandatoryCriterionStandard
          : (tableData[rowIndex]?.scNonMandatoryCriterionStandard || "");
        const isStandardNA = String(rowStandard).toLowerCase() === "na";

        const displayValue = isStandardNA ? "na" : (value || "");

        return (
          <CustomSelect
            name="scNonMandatoryCriterionNumber"
            value={displayValue}
            placeholder="Select Non-Mandatory Criterion Number"
            options={isStandardNA ? [{ value: "na", label: "na" }] : scNonMandatoryCriterionNumbers}
            disabled={isStandardNA}
            onChange={(e) => {
              const selectedVal = e.target.value;
              onChange(selectedVal);
              
              if (selectedVal && String(selectedVal).toLowerCase() !== "na") {
                const opt = (scNonMandatoryCriterionNumbers as any[]).find((o: any) => o.value === selectedVal);
                const desc = opt ? (opt.title || "") : "";
                const type = opt ? (opt.type || "") : "";
                formik.setFieldValue("scNonMandatoryCriterionDescription", desc);
                formik.setFieldValue("scNonMandatoryCriterionType", type);
                setData((prevData) =>
                  prevData.map((row, idx) =>
                    idx === rowIndex
                      ? {
                          ...row,
                          scNonMandatoryCriterionNumber: selectedVal as string,
                          scNonMandatoryCriterionDescription: desc as string,
                          scNonMandatoryCriterionType: type as string,
                        }
                      : row
                  )
                );
              } else {
                formik.setFieldValue("scNonMandatoryCriterionDescription", "");
                formik.setFieldValue("scNonMandatoryCriterionType", "");
                setData((prevData) =>
                  prevData.map((row, idx) =>
                    idx === rowIndex
                      ? {
                          ...row,
                          scNonMandatoryCriterionNumber: selectedVal as string,
                          scNonMandatoryCriterionDescription: "",
                          scNonMandatoryCriterionType: "",
                        }
                      : row
                  )
                );
              }
            }}
          />
        );
      },
    },
    {
      accessorKey: "scNonMandatoryCriterionDescription",
      header: "Description",
      Header: <span>Description</span>,
      renderEditCell: ({ value }) => (
        <Box sx={{ p: 1, color: "text.secondary" }}>{value || ""}</Box>
      ),
    },
    {
      accessorKey: "scNonMandatoryCriterionType",
      header: "Type",
      Header: <span>Type</span>,
      renderEditCell: ({ value }) => (
        <Box sx={{ p: 1, color: "text.secondary" }}>{value || ""}</Box>
      ),
    },
    {
      accessorKey: "scNonMandatoryCriterionStatus",
      header: "Criterion Status",
      Header: (
        <span>
          <i style={{ color: 'red' }}>* </i>
          <abbr title="SC Non-Mandatory Criterion Status" style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
            Criterion Status
          </abbr>
        </span>
      ),
      renderEditCell: ({ value, onChange, rowIndex }) => {
        const isEditingThisRow = editingRowIndex === rowIndex;
        const rowStandard = isEditingThisRow
          ? formik.values.scNonMandatoryCriterionStandard
          : (tableData[rowIndex]?.scNonMandatoryCriterionStandard || "");
        const isStandardNA = String(rowStandard).toLowerCase() === "na";

        const displayValue = isStandardNA ? "na" : (value || "");

        return (
          <CustomSelect
            name="scNonMandatoryCriterionStatus"
            value={displayValue}
            placeholder="Select Non-Mandatory Criterion Status"
            options={isStandardNA ? [{ value: "na", label: "na" }] : scNonMandatoryCriterionStatusValues}
            disabled={isStandardNA}
            onChange={(e) => {
              onChange(e.target.value)
            }}
          />
        );
      },
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setRestrictPopUpSave(true);
    setEditingRowIndex(rowIndex);
    
    // Ensure all required fields are set in formik
    const rowData = data[rowIndex];
    const formikValues = {
      scNonMandatoryCriterionStandard: rowData.scNonMandatoryCriterionStandard || "",
      scNonMandatoryCriterionStandardDescription: rowData.scNonMandatoryCriterionStandardDescription || "",
      scNonMandatoryCriterionVersion: rowData.scNonMandatoryCriterionVersion || "",
      scNonMandatoryCriterionNumber: rowData.scNonMandatoryCriterionNumber || "",
      scNonMandatoryCriterionStatus: rowData.scNonMandatoryCriterionStatus || "",
      scNonMandatoryCriterionStatusDescription: rowData.scNonMandatoryCriterionStatusDescription || "",
      scNonMandatoryCriterionDescription: rowData.scNonMandatoryCriterionDescription || "",
      scNonMandatoryCriterionType: rowData.scNonMandatoryCriterionType || "",
    };
    
    formik.setValues(formikValues);
    
    // Reset touched state to allow validation
    formik.setTouched({});
    
    // Force formik to revalidate
    setTimeout(() => {
      formik.validateForm();
    }, 100);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    
    // Call the parent function to update the data
    setNonMandatoryCriterionData(updatedData);
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton onClick={handleAddNewRow} sx={{ mb: 2 }} disabled={!formik.isValid || hasSubmitSC || isRestrictPopUpSave || isEditingDisabled || (data?.some(r => String(r.scNonMandatoryCriterionStandard).toLowerCase() === 'na'))}>
            Add Non-Mandatory Criterion Information
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
          enableEditing={!isEditingDisabled}
          hideDelete={isEditingDisabled}
        />
      </Box>
    </div>
  );
};

export default NonMandatoryCriterionDetailsForm; 